<script setup>
/**
 * Panel flotante de Plantillas de Mensajes en el chat (Victor 2026-05-23).
 * Propuesta 2 · Grid ejecutivo M1.B. Solo LEE plantillas y emite la elegida;
 * la inserción en el cuadro la hace ReplyBox (revisa/edita antes de enviar).
 */
import { ref, computed, onMounted } from 'vue';
import MessageTemplatesAPI from 'dashboard/api/messageTemplates';

const props = defineProps({
  branchId: { type: [Number, String], default: null },
});
const emit = defineEmits(['select', 'close']);

const ICONS = {
  'Saludo inicial': '👋', 'Información de paquetes': '📦', 'Confirmación de cita': '📅',
  'Recordatorio de sesión': '⏰', 'Ubicación de sucursal': '📍', 'Formas de pago': '💳',
  'Seguimiento a cliente': '🔔', 'Cliente sin respuesta': '🔔', 'Promociones': '🎁',
};

const all = ref([]);
const loading = ref(true);
const search = ref('');
const cat = ref(null);

const fetchTemplates = async () => {
  loading.value = true;
  try {
    const { data } = await MessageTemplatesAPI.get();
    const bid = props.branchId ? Number(props.branchId) : null;
    all.value = (data || []).filter(
      t => t.active && (t.branch_id === null || t.branch_id === bid)
    );
  } catch (e) {
    all.value = [];
  } finally {
    loading.value = false;
  }
};

const categories = computed(() => [...new Set(all.value.map(t => t.category).filter(Boolean))]);
const filtered = computed(() => {
  let list = all.value;
  if (cat.value) list = list.filter(t => t.category === cat.value);
  if (search.value) {
    const q = search.value.toLowerCase();
    list = list.filter(t => (t.title || '').toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q));
  }
  return list;
});
const iconFor = c => ICONS[c] || '✦';
const snippet = b => (b || '').length > 110 ? `${b.slice(0, 110)}…` : b;

onMounted(fetchTemplates);
</script>

<template>
  <div class="mtp-overlay" @click.self="emit('close')">
    <div class="mtp card">
      <div class="mtp-head">
        <div class="search">
          <span class="ic">🔎</span>
          <input v-model="search" placeholder="Buscar plantilla…" />
        </div>
        <button class="mx" @click="emit('close')">✕</button>
      </div>
      <div class="cats">
        <span class="chip" :class="{ on: !cat }" @click="cat = null">Todas</span>
        <span v-for="c in categories" :key="c" class="chip" :class="{ on: cat === c }" @click="cat = c">{{ c }}</span>
      </div>
      <div class="body">
        <div v-if="loading" class="empty">Cargando…</div>
        <div v-else-if="!filtered.length" class="empty">No hay plantillas para esta sucursal.</div>
        <div v-else class="grid">
          <div v-for="t in filtered" :key="t.id" class="gcard" @click="emit('select', t)">
            <div class="gh">
              <div class="gico">{{ iconFor(t.category) }}</div>
              <span class="bpill" :class="{ lr: t.branch_id }">{{ t.branch_id ? 'Sucursal' : 'Todas' }}</span>
            </div>
            <div class="gtt">{{ t.title }}</div>
            <div class="gpv">{{ snippet(t.body) }}</div>
            <div class="gfoot">
              <span class="cpill">{{ t.category || '—' }}</span>
              <button class="use" @click.stop="emit('select', t)">Usar</button>
            </div>
          </div>
        </div>
      </div>
      <div class="foot">El mensaje se coloca en el cuadro — revísalo y edítalo antes de enviar.</div>
    </div>
  </div>
</template>

<style scoped>
.mtp-overlay {
  position: absolute; left: 0; right: 0; bottom: calc(100% + 8px); z-index: 40;
  display: flex; justify-content: center;
}
.mtp {
  --gold: #fbbf24; --gold-d: #d97706; --g: #22c55e; --blue: #60a5fa;
  --w55: rgba(255, 255, 255, 0.55); --w40: rgba(255, 255, 255, 0.4); --bd: rgba(255, 255, 255, 0.1);
  position: relative; width: min(720px, 96%); max-height: 440px; display: flex; flex-direction: column;
  background: rgba(10, 10, 10, 0.96); border: 1.5px solid var(--bd); border-radius: 16px;
  box-shadow: 0 -10px 50px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.04) inset; overflow: hidden;
  color: #fff; font-family: -apple-system, system-ui, sans-serif;
}
.mtp-head { display: flex; align-items: center; gap: 10px; padding: 13px 15px; border-bottom: 1px solid var(--bd); }
.search { flex: 1; display: flex; align-items: center; gap: 9px; background: rgba(255, 255, 255, 0.05); border: 1px solid var(--bd); border-radius: 10px; padding: 9px 13px; }
.search input { flex: 1; background: none; border: none; outline: none; color: #fff; font-size: 13px; font-family: inherit; }
.search input::placeholder { color: var(--w40); } .ic { color: var(--w40); }
.mx { width: 32px; height: 32px; border-radius: 9px; border: 1px solid var(--bd); background: rgba(255, 255, 255, 0.04); color: var(--w55); cursor: pointer; font-size: 14px; }
.mx:hover { color: var(--gold); border-color: rgba(251, 191, 36, 0.3); }
.cats { display: flex; gap: 7px; padding: 12px 15px; overflow-x: auto; border-bottom: 1px solid var(--bd); }
.chip { flex: none; padding: 6px 12px; border-radius: 8px; font-size: 11px; font-weight: 700; color: var(--w55); background: rgba(255, 255, 255, 0.04); border: 1px solid var(--bd); cursor: pointer; transition: 0.15s; }
.chip:hover { color: #fff; } .chip.on { color: var(--gold); background: rgba(251, 191, 36, 0.1); border-color: rgba(251, 191, 36, 0.3); }
.body { overflow: auto; padding: 13px 15px; }
.empty { padding: 30px; text-align: center; color: var(--w55); font-size: 13px; }
.grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.gcard { position: relative; padding: 14px 15px; border-radius: 13px; background: rgba(255, 255, 255, 0.03); border: 1px solid var(--bd); cursor: pointer; transition: 0.18s; }
.gcard:hover { transform: translateY(-3px); border-color: rgba(251, 191, 36, 0.35); background: rgba(251, 191, 36, 0.05); }
.gh { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.gico { width: 36px; height: 36px; border-radius: 11px; display: grid; place-items: center; font-size: 16px; background: linear-gradient(135deg, rgba(251, 191, 36, 0.18), rgba(217, 119, 6, 0.1)); border: 1px solid rgba(251, 191, 36, 0.25); }
.gtt { font-size: 13px; font-weight: 800; margin-bottom: 6px; }
.gpv { font-size: 11.5px; color: var(--w55); line-height: 1.5; min-height: 50px; }
.gfoot { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; }
.cpill { display: inline-flex; padding: 2px 8px; border-radius: 6px; background: rgba(96, 165, 250, 0.1); color: var(--blue); border: 1px solid rgba(96, 165, 250, 0.3); font-size: 9.5px; font-weight: 800; }
.bpill { display: inline-flex; padding: 2px 8px; border-radius: 6px; background: rgba(251, 191, 36, 0.1); color: var(--gold); border: 1px solid rgba(251, 191, 36, 0.3); font-size: 9.5px; font-weight: 800; }
.bpill.lr { background: rgba(34, 197, 94, 0.1); border-color: rgba(34, 197, 94, 0.3); color: var(--g); }
.use { background: linear-gradient(135deg, var(--gold), var(--gold-d)); color: #000; border: none; padding: 7px 13px; border-radius: 8px; font-size: 11px; font-weight: 800; cursor: pointer; }
.use:hover { transform: translateY(-1px); }
.foot { padding: 11px 15px; border-top: 1px solid var(--bd); font-size: 10.5px; color: var(--w40); background: rgba(251, 191, 36, 0.04); }
</style>
